import { useState } from 'react';
import { X, Mail, Check } from 'lucide-react';
import * as api from '../lib/api';
import type { User } from '../lib/types';

interface Props {
  user: User;
  onClose: () => void;
  onLinked: (email: string) => void;
}

export default function EmailLinkModal({ user, onClose, onLinked }: Props) {
  const [step, setStep] = useState<'input' | 'otp' | 'merge-confirm' | 'done'>('input');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [conflictUsers, setConflictUsers] = useState<any[]>([]);
  const [verifiedAuthUid, setVerifiedAuthUid] = useState<string | null>(null);

  const handleSendOtp = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) { setError('Введите корректный email'); return; }
    setLoading(true);
    setError('');
    try {
      await api.sendEmailOtp(trimmed);
      setStep('otp');
    } catch (e: any) {
      setError(e.message || 'Ошибка отправки');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    const code = otpCode.replace(/\D/g, '');
    if (code.length < 6) { setError('Введите код'); return; }
    setLoading(true);
    setError('');
    try {
      const data = await api.verifyEmailOtp(email.trim().toLowerCase(), code);
      if (!data.user) throw new Error('Ошибка подтверждения');

      setVerifiedAuthUid(data.user.id);

      const { supabase } = await import('../lib/supabase');
      const { data: existingUsers } = await supabase
        .from('users').select('id, email, display_name, first_name, last_name, username, is_guest').eq('email', email.trim().toLowerCase());

      const conflicts = (existingUsers || []).filter((u: any) => u.id !== user.id);

      if (conflicts.length > 0) {
        setConflictUsers(conflicts);
        setStep('merge-confirm');
      } else {
        await supabase.from('users').update({
          email: email.trim().toLowerCase(),
          auth_uid: data.user.id,
        }).eq('id', user.id);
        setStep('done');
        onLinked(email.trim().toLowerCase());
      }
    } catch (e: any) {
      setError(e.message === 'Token has expired or is invalid'
        ? 'Неверный или просроченный код' : (e.message || 'Ошибка'));
      setOtpCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    setLoading(true);
    setError('');
    try {
      for (const cu of conflictUsers) {
        await api.mergeAccounts(user.id, cu.id);
      }
      const { supabase } = await import('../lib/supabase');
      await supabase.from('users').update({
        email: email.trim().toLowerCase(),
        auth_uid: verifiedAuthUid,
      }).eq('id', user.id);
      setStep('done');
      onLinked(email.trim().toLowerCase());
    } catch (e: any) {
      setError(e.message || 'Ошибка объединения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="elm-overlay" onClick={onClose}>
      <div className="elm-modal" onClick={e => e.stopPropagation()}>
        <button className="elm-close" onClick={onClose}><X size={20} /></button>

        <div className="elm-icon"><Mail size={28} /></div>

        {step === 'done' ? (
          <>
            <div className="elm-title" style={{ color: 'var(--hu-success)' }}>
              <Check size={20} /> Email привязан
            </div>
            <div className="elm-subtitle">{email}</div>
            <div className="elm-hint">Теперь вы можете входить через браузер по этому email</div>
            <button className="elm-btn" onClick={onClose}>Готово</button>
          </>
        ) : step === 'merge-confirm' ? (
          <>
            <div className="elm-title">Объединить аккаунты?</div>
            <div className="elm-hint">
              <b>{email}</b> уже используется другим аккаунтом. Все данные (рейтинг, монеты, турниры) будут перенесены в текущий аккаунт.
            </div>
            {error && <div className="elm-error">{error}</div>}
            <div className="elm-buttons">
              <button className="elm-btn-secondary" onClick={() => { setStep('input'); setConflictUsers([]); }}>Отмена</button>
              <button className="elm-btn" onClick={handleMerge} disabled={loading}>
                {loading ? 'Объединение...' : 'Объединить'}
              </button>
            </div>
          </>
        ) : step === 'otp' ? (
          <>
            <div className="elm-title">Введите код</div>
            <div className="elm-subtitle">Отправлен на {email}</div>
            <input
              type="text"
              inputMode="numeric"
              placeholder="Код"
              value={otpCode}
              onChange={e => { setOtpCode(e.target.value.replace(/\D/g, '')); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              className="elm-input"
              style={{ textAlign: 'center', letterSpacing: 6, fontWeight: 700, fontSize: 20 }}
              autoFocus
            />
            {error && <div className="elm-error">{error}</div>}
            <button className="elm-btn" onClick={handleVerify} disabled={loading || otpCode.length < 6}>
              {loading ? 'Проверка...' : 'Подтвердить'}
            </button>
            <button className="elm-link" onClick={() => { setStep('input'); setOtpCode(''); setError(''); }}>
              ← Изменить email
            </button>
          </>
        ) : (
          <>
            <div className="elm-title">Привязать email</div>
            <div className="elm-hint">
              Привяжите email, чтобы входить в приложение через браузер без Telegram. Ваш аккаунт и прогресс будут синхронизированы.
            </div>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
              className="elm-input"
              autoFocus
            />
            {error && <div className="elm-error">{error}</div>}
            <button className="elm-btn" onClick={handleSendOtp} disabled={loading || !email.trim()}>
              {loading ? 'Отправка...' : 'Получить код'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
